import { Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React from "react";
import { useAuth } from "../context/AuthContext";
import { PlayerStatistic } from "../interfaces";
import logo from "../media/steeldart_logo_baden_wuerttemberg.png";

interface PlayerStatisticsDownloadButtonProps {
  singleColumns: GridColDef<PlayerStatistic>[];
  doubleColumns: GridColDef<PlayerStatistic>[];
  rows: PlayerStatistic[];
  season: string;
  team_name: string;
  singleSortModel: any;
  doubleSortModel: any;
}

const PlayerStatisticsDownloadButton: React.FC<
  PlayerStatisticsDownloadButtonProps
> = ({
  singleColumns,
  doubleColumns,
  rows,
  season,
  team_name,
  singleSortModel,
  doubleSortModel,
}) => {
  const { isAuthenticated } = useAuth();
  const showOnlyPlayerFirstName = (name: String) => {
    return name.split(" ")[0];
  };
  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginLeft = 14;
    const marginTop = 18;

    // Header
    doc.setFontSize(15);
    doc.text("Württemberg-Dart-Liga", marginLeft, marginTop);

    doc.setFontSize(12);
    const subtitle = `Statistik - Saison ${season} - ${team_name}`;
    const subtitleWidth = doc.getTextWidth(subtitle);
    doc.text(subtitle, (pageWidth - subtitleWidth) / 2, marginTop);

    const formatCellValue = (value: any, field: any) => {
      const percentageFields = [
        "single_matches_won_pct",
        "single_legs_won_pct",

        "double_matches_won_pct",
        "double_legs_won_pct",
      ];
      let formatted = "";

      if (Array.isArray(value) && value.length > 0) {
        formatted = value.toString();
      } else if (Array.isArray(value) && value.length === 0) {
        formatted = "";
      } else if (typeof value === "object" && value !== null) {
        if ("name" in value)
          formatted = isAuthenticated
            ? value.name
            : showOnlyPlayerFirstName(value.name);
        else formatted = JSON.stringify(value);
      } else {
        formatted =
          value !== undefined && value !== null ? value.toString() : "";
      }

      // Append % if this field is in the percentageFields list
      if (percentageFields.includes(field) && formatted !== "") {
        formatted += "%";
      }
      return formatted;
      // if (Array.isArray(value) && value.length > 0) {
      //   return value.toString();
      // } else if (Array.isArray(value) && value.length === 0) {
      //   return "";
      // }
      // if (typeof value === "object" && value !== null) {
      //   if ("name" in value) return value.name;
      //   return JSON.stringify(value);
      // }
      // return value !== undefined && value !== null ? value.toString() : "";
    };

    // Sorting helper
    const sortRows = (rows: PlayerStatistic[], sortModel: any) => {
      if (!sortModel || sortModel.length === 0) return rows;
      const { field, sort } = sortModel[0];
      return [...rows].sort((a, b) => {
        const aValue = a[field as keyof PlayerStatistic] as number;
        const bValue = b[field as keyof PlayerStatistic] as number;
        return sort === "asc" ? aValue - bValue : bValue - aValue;
      });
    };

    const sortedSingleRows = sortRows(rows, singleSortModel);
    const sortedDoubleRows = sortRows(rows, doubleSortModel);

    // Common table options
    const tableOptions = {
      styles: { fontSize: 9, cellPadding: 2, overflow: "linebreak" as const },
      headStyles: {
        fillColor: [220, 220, 220] as [number, number, number],
        textColor: 0,
        overflow: "linebreak" as const,
        // cellPadding: [2, 1, 2, 1],
      },
      columnStyles: {
        0: {
          cellWidth: 40,
        },
        1: {
          cellWidth: 20,
        },
        2: {
          cellWidth: 20,
        },
        3: {
          cellWidth: 20,
        },
        4: {
          cellWidth: 20,
        },
        5: {
          cellWidth: 20,
        },
        6: {
          cellWidth: 20,
        },
        7: {
          cellWidth: 20,
        },
        8: {
          cellWidth: 30,
        },
        9: {
          cellWidth: 20,
        },
      },
      // margin: { left: marginLeft, right: 14 },
    };

    // ----- First table -----
    autoTable(doc as any, {
      ...tableOptions,
      head: [singleColumns.map((c) => c.headerName || "")],
      body: sortedSingleRows.map((row) =>
        singleColumns.map((col) =>
          formatCellValue(row[col.field as keyof PlayerStatistic], col.field)
        )
      ),
      startY: marginTop + 6,
    });

    // ----- Second table -----
    const lastY = (doc as any).lastAutoTable?.finalY || marginTop + 6;

    autoTable(doc as any, {
      ...tableOptions,
      head: [doubleColumns.map((c) => c.headerName || "")],
      body: sortedDoubleRows.map((row) =>
        doubleColumns.map((col) =>
          formatCellValue(row[col.field as keyof PlayerStatistic], col.field)
        )
      ),
      startY: lastY + 8,
      tableWidth: "auto",
    });

    // ----- Ligaleitung section -----
    // ----- Ligaleitung section -----
    const ligaleitungText = [
      "Ligaleitung:",
      "Ferdinand Schneider",
      "Telefon: +491791026676",
      "E-Mail: wuerttemberg-dart-liga@gmx.de",
    ];

    doc.setFontSize(10);
    const footerMargin = 10;

    // Logo placement
    const imgWidth = 20;
    const imgHeight = 20;
    const imgX = pageWidth - imgWidth - footerMargin + 2;
    const imgY = pageHeight - imgHeight - ligaleitungText.length * 5;
    // place it slightly above the text block

    doc.addImage(logo, "PNG", imgX, imgY, imgWidth, imgHeight);

    // Text placement (bottom-right)
    let currentY = pageHeight - ligaleitungText.length * 5;
    ligaleitungText.forEach((line) => {
      const textWidth = doc.getTextWidth(line);
      const textX = pageWidth - textWidth - footerMargin;
      doc.text(line, textX, currentY);
      currentY += 5;
    });

    doc.save(`${team_name}_statistik_${season}`);
  };

  return (
    <Button
      onClick={handleExportPDF}
      style={{ marginBottom: 10, textTransform: "none" }}
    >
      Als PDF exportieren
    </Button>
  );
};

export default PlayerStatisticsDownloadButton;
